import type { HTMLAttributes, ReactNode } from 'react'

// `title` is omitted from the DOM attributes on purpose: the HTML one is a
// tooltip string, this one is a heading node, and letting the two share a name
// would either narrow this to `string` or put a tooltip on the panel.
interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode
  /**
   * `h2` by default. `h3` when the panel already sits under a card title, so
   * the document outline does not skip or repeat a level.
   */
  titleAs?: 'h2' | 'h3'
  /** The BrainSpot mark above the title. Decoration: empty `alt`. */
  mark?: boolean
  align?: 'center' | 'start'
  /** A button or link under the body. */
  action?: ReactNode
}

/**
 * "There is nothing here yet."
 *
 * The title is a REAL HEADING rather than the design's `.empty strong`: an
 * outcome title is a heading, and a screen reader's heading list is how someone
 * finds out that a page is empty rather than broken.
 *
 * NO ROLE AND NO LIVE REGION. This is a rendering of a state the page is in,
 * not an announcement that something just happened, and `role="status"` here
 * would re-announce on every re-render. The pages that do need to announce a
 * transition own their own live region.
 */
export function EmptyState({
  title,
  titleAs: Title = 'h2',
  mark = false,
  align = 'center',
  action,
  children,
  className,
  ...rest
}: EmptyStateProps) {
  const classes = [
    'empty',
    align === 'start' && 'empty--start',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div {...rest} className={classes}>
      {/* Root-absolute, as `AppShell.tsx` serves the wordmark: a document-relative
          src 404s on a nested route such as /history/:id. */}
      {mark && <img className="empty__mark" src="/mark-small.svg" alt="" />}
      <Title className="empty__title">{title}</Title>
      {children}
      {action}
    </div>
  )
}
